<!-- Usage:
  Variables:
    selectedDay = 5
    items = [
      { value: 5, label: 'Dia 5' },
      { value: 10, label: 'Dia 10' },
      { value: 15, label: 'Dia 15' },
    ]

  Template:
    - Dropdown Input
      <z-dropdown
        :items="availableDays"
        placeholder="Selecione o dia"
        v-model="selectedDay"
      />
    - Dropdown Link
      <z-dropdown
        :items="availableDays"
        placeholder="Selecione o dia"
        v-model="selectedDay"
        link
      />
-->

<template>
  <div class="z-dropdown" :class="{ 'z-dropdown--inline-block': inline }">
    <!-- Dropdown Link -->
    <div
      class="z-dropdown__link"
      tabindex="0"
      @click="toggle()"
      @blur="close()"
      v-if="link"
    >
      <span v-if="!selectedItem">{{ placeholder }}</span>
      <span v-else>{{ selectedItem.label }}</span>
      <z-icon
        :icon="['fas', 'chevron-down']"
        :class="{ 'z-dropdown-arrow--up': active}"
        class="z-dropdown__arrow"
        size="medium"
      />
    </div>

    <!-- Dropdown Input -->
    <div
      :class="{ 'z-dropdown__input--active': active }"
      class="z-dropdown__input"
      tabindex="0"
      @click="toggle()"
      @blur="close()"
      v-else
    >
      <span class="z-dropdown__placeholder" v-if="!selectedItem">{{ placeholder }}</span>
      <span class="z-dropdown__value" v-else>{{ selectedItem.label }}</span>
      <z-icon
        :icon="['fas', 'chevron-down']"
        :class="{ 'z-dropdown-arrow--up': active}"
        size="medium"
        class="z-dropdown__arrow"
      />
    </div>

    <!-- Dropdown menu -->
    <div
      :class="[{ 'z-dropdown__menu--show': active }, { 'z-dropdown__menu--link': link }]"
      class="z-dropdown__menu"
    >
      <div
        :class="{ 'z-dropdown__item--active': isCurrent(item) }"
        :key="index"
        class="z-dropdown__item"
        v-for="(item, index) in items"
        @mousedown="selectItem(item)"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@quero/zilla-core/src/atoms/z-input';
@import '@quero/zilla-core/src/atoms/z-text';
@import '@quero/zilla-core/src/atoms/z-link';

.z-dropdown {
  position: relative;
}

.z-dropdown--inline-block {
  display: inline-block;
}

.z-dropdown__input {
  @extend .z-input;
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;

  &:focus {
    outline: none;
  }
}

.z-dropdown__input--active {
  &,
  &:focus,
  &:active,
  &:visited,
  &:hover {
    border: 1px solid var(--color-primary);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.z-dropdown__link {
  @extend .z-link;
  cursor: pointer;
  display: inline;
}

.z-dropdown__arrow {
  color: inherit;
  vertical-align: middle;
}

.z-dropdown-arrow--up {
  transform: rotate(180deg);
}

.z-dropdown__value {
  @extend .z-text;
}

.z-dropdown__placeholder,
.z-dropdown__value {
  font-size: rem(16);
  line-height: rem(24);
}

.z-dropdown__menu {
  background-color: var(--color-neutral-sugar);
  border-bottom-left-radius: var(--size-border-radius-medium);;
  border-bottom-right-radius: var(--size-border-radius-medium);;
  border-color: var(--color-primary);
  border-style: solid;
  border-top: 0;
  border-width: 1px;
  display: none;
  left: 0;
  min-width: 160px;
  position: absolute;
  width: 100%;
  z-index: 1;
}

.z-dropdown__menu--link {
  border-top: 1px solid var(--color-primary);
  border-top-left-radius: var(--size-border-radius-medium);;
  border-top-right-radius: var(--size-border-radius-medium);;
  top: calc(100% + #{var(--space-small)});
  width: auto;
}

.z-dropdown__menu--show {
  display: block;
}

.z-dropdown__item {
  color: var(--color-neutral-black);
  cursor: pointer;
  font-size: rem(16);
  line-height: rem(24);
  padding: var(--space-medium);
  white-space: nowrap;

  &:not(:last-of-type) {
    border-bottom: 2px solid var(--color-neutral-lighter-gray);
  }

  &:hover {
    @extend .z-dropdown__item--active;
  }
}

.z-dropdown__item--active {
  background-color: var(--color-primary);
  color: var(--color-neutral-sugar);
}

</style>

<script>
/* eslint-disable vue/require-prop-types */
export default {
  props: {
    value: {
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      required: true,
    },
    link: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      active: false,
      selectedItem: null,
    };
  },
  methods: {
    close() {
      this.active = false;
    },
    toggle() {
      this.active = !this.active;
    },
    isCurrent(item) {
      return item === this.selectedItem;
    },
    selectItem(item) {
      this.active = false;
      this.selectedItem = item;
      this.$emit('input', item.value);
    },
  },
  mounted() {
    if (this.value) {
      [this.selectedItem] = this.items.filter((item) => item.value === this.value);
    }
  },
};
</script>
